import React, {useMemo} from 'react';
import type {FieldError, PartBool} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {
  toFieldError,
  validateBool,
} from 'schemaDefinition/functions/validators';
import {CheckboxField} from 'components';
import {useUniqueFieldId} from 'components/fields/hooks/useUniqueFieldId';
import {useFormSchemaGlobalScopeContext} from 'schema/contexts';
import {getBoxLabel} from 'schema/form/functions';
import {useShowFieldErrors} from 'schema/form/hooks';
import type {BaseFieldProps} from './types';

type Props = BaseFieldProps & {
  part: PartBool;
};

export const Checkbox: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  label,
  mode,
  layout,
  localScope,
  globalScope,
  relatedScope,
}) => {
  const showFieldErrors = useShowFieldErrors(true);
  const localization = useLocalization();
  const {schema} = useFormSchemaGlobalScopeContext();
  const fieldId = useUniqueFieldId(valuePath);

  const boxLabel = useMemo(
    () => getBoxLabel(localization, part, schema),
    [localization, part, schema],
  );

  const error = useMemo((): FieldError => {
    if (showFieldErrors) {
      const valid = validateBool(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
      }).valid;

      return toFieldError(valid);
    }
    return false;
  }, [
    globalScope,
    part,
    relatedScope,
    localScope,
    showFieldErrors,
    value,
    valuePath,
  ]);

  return (
    <CheckboxField
      fieldId={fieldId}
      name={valuePath}
      label={label ?? ''}
      boxLabel={boxLabel}
      layout={layout}
      readonly={mode === 'read-only'}
      value={!!value}
      onChange={newVal => setFieldValue(valuePath, newVal)}
      error={error}
    />
  );
};
